import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useCheck2fa } from '@/features/auth';
import { useAppSelector } from '@/app/hooks';
import { Button } from '@/shared/ui/Button';
import CheckboxCommon from '@/shared/ui/Checkbox/CheckboxCommon';
import { AuthInput } from './ui/AuthInput';
import { AuthLayout } from './ui/AuthLayout';
import { authFormClassName, authServerErrorClassName } from './ui/authForm';
import type { AuthState } from '@/features/auth';

// TwoFactorAuth на новом стеке. Читает pending2fa из RTK store (туда его кладёт
// useLogin при outcome 'need-2fa'). useCheck2fa посылает code+temporaryToken+
// rememberMe на /user/check2fa, на success ставит auth_key cookie и переводит
// store в authenticated.
//
// Прямой переход на /auth/2fa без предварительного login → отсюда нет
// pending2fa → редирект на /login.

const createTwoFactorSchema = (t: TFunction<'auth'>) =>
  z.object({
    code: z.string().trim().min(1, t('validation.codeRequired')),
  });

type TwoFactorFormData = z.infer<ReturnType<typeof createTwoFactorSchema>>;

const selectAuth = (s: { auth: AuthState }) => s.auth;

export function TwoFactorAuth() {
  const { t } = useTranslation(['auth', 'common']);
  const twoFactorSchema = useMemo(() => createTwoFactorSchema(t), [t]);
  const navigate = useNavigate();
  const { status, pending2fa } = useAppSelector(selectAuth);
  const check2faMutation = useCheck2fa();
  const [rememberMe, setRememberMe] = useState<boolean>(
    pending2fa?.rememberMe ?? false,
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<TwoFactorFormData>({
    resolver: zodResolver(twoFactorSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: { code: '' },
  });

  // Guard: статус 'authenticated' → юзер уже залогинен (например, после
  // повторного check2fa) → в кабинет. Если статус не 'pending-2fa' и не
  // 'authenticated' — значит юзер пришёл сюда напрямую без login-flow,
  // отправляем на /login.
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }
  if (status !== 'pending-2fa' || !pending2fa) {
    return <Navigate to="/login" replace />;
  }

  const handleInputChange = (field: keyof TwoFactorFormData) => {
    if (errors.root?.server) clearErrors('root.server');
    if (errors[field]) clearErrors(field);
  };

  const getShowError = (field: keyof TwoFactorFormData) =>
    !!errors[field] || !!errors.root?.server;

  const onSubmit = async (data: TwoFactorFormData) => {
    clearErrors('root.server');
    try {
      const outcome = await check2faMutation.mutateAsync({
        code2fa: data.code,
        temporaryToken: pending2fa.temporaryToken,
        rememberMe,
      });
      if (outcome.kind === 'authenticated') {
        navigate('/', { replace: true });
      } else {
        setFormError('root.server', {
          type: 'manual',
          message: t('twoFactor.invalidCode'),
        });
      }
    } catch {
      setFormError('root.server', {
        type: 'manual',
        message: t('common:networkError'),
      });
    }
  };

  return (
    <AuthLayout
      title={t('twoFactor.title')}
      questionText={t('noAccount')}
      linkTo="/register"
      linkText={t('signUp')}
      hint={t('twoFactor.hint')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={authFormClassName()}
      >
        {errors.root?.server && (
          <div className={authServerErrorClassName()}>
            {errors.root.server.message}
          </div>
        )}

        <div className="relative w-full">
          <AuthInput
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            label={t('fields.secretCode')}
            example={t('examples.secretCode')}
            error={errors.code?.message}
            showError={getShowError('code')}
            {...register('code', { onChange: () => handleInputChange('code') })}
          />
        </div>

        <div className="flex items-center gap-1">
          <CheckboxCommon
            checked={rememberMe}
            onChange={setRememberMe}
            color="primary"
            label={t('rememberMe')}
            colorLabel="content-auth-checkbox"
          />
        </div>

        <div className="mt-[6px]">
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="min-h-[48px] w-full !px-0"
          >
            {t('signIn')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
